/**
 * Staff-only picker: choose which portal user to view the portal as.
 *
 * Staff carry the RLS bypass, so an unscoped portal session would show every
 * customer at once — never what a customer actually sees. This page lists
 * portal logins (portal_users_view) and starts a view-as session; the layout
 * redirects staff here whenever no target is selected.
 */
import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { EmptyState, resolveAppUrl } from '@pazportal/ui';
import { useAuth } from '../contexts/AuthContext';
import { useViewAs } from '../contexts/ViewAsContext';
import { usePortalUsers, type PortalUser } from '../hooks/usePortalUsers';
import { useI18n } from '../i18n/I18nProvider';
import { SkeletonRow } from '../components/Skeleton';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { formatDateTime } from '../lib/formatters';

const MAX_ACCOUNT_NAMES = 3;

function matches(user: PortalUser, needle: string): boolean {
  if (!needle) return true;
  const haystack = [user.email, user.full_name ?? '', ...user.account_names]
    .join(' ')
    .toLowerCase();
  return haystack.includes(needle);
}

export function StaffViewAsPage() {
  const { user, signOut } = useAuth();
  const { isStaff, setViewAsUser, notice, clearNotice } = useViewAs();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { data: users, isLoading, isError, error, refetch } = usePortalUsers();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (users ?? []).filter((u) => u.active_accounts > 0 && matches(u, needle));
  }, [users, query]);

  if (!isStaff) return <Navigate to="/dashboard" replace />;

  const adminHome = resolveAppUrl('admin', '/', { override: import.meta.env.VITE_ADMIN_URL });

  const choose = (target: PortalUser) => {
    setViewAsUser(target.user_id);
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-[var(--paz-vh,100vh)] bg-bg text-text">
      <header className="border-b border-border bg-surface">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3 px-4 py-3">
          <span className="text-sm font-semibold text-primary">PazNetworks, Inc.</span>
          <span className="text-xs text-text-muted">{t('staff.picker.eyebrow')}</span>
          <div className="ml-auto flex items-center gap-2">
            <LanguageSwitcher />
            <a
              href={adminHome}
              className="inline-flex min-h-11 items-center rounded-md border border-border bg-surface px-3 text-sm font-medium text-text hover:bg-surface-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:min-h-9"
            >
              {t('staff.picker.back_admin')}
            </a>
            <button
              type="button"
              onClick={() => void signOut()}
              className="inline-flex min-h-11 items-center rounded-md px-3 text-sm text-text-muted hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:min-h-9"
            >
              {t('common.sign_out')}
            </button>
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto max-w-5xl space-y-5 px-4 py-6">
        <div>
          <h1 className="text-xl font-semibold text-text">{t('staff.picker.title')}</h1>
          <p className="mt-1 max-w-3xl text-sm text-text-muted">{t('staff.picker.subtitle')}</p>
          {user?.email && (
            <p className="mt-1 text-xs text-text-faint">
              {t('common.signed_in_as')} {user.email}
            </p>
          )}
        </div>

        {notice === 'user_not_found' && (
          <div
            role="alert"
            className="flex items-start justify-between gap-3 rounded-md border border-notification bg-notification-soft px-3 py-2 text-sm text-notification"
          >
            <span>{t('staff.picker.not_found')}</span>
            <button
              type="button"
              onClick={clearNotice}
              className="min-h-11 shrink-0 px-2 text-xs font-medium underline md:min-h-6"
            >
              {t('common.close')}
            </button>
          </div>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="w-full sm:max-w-md">
            <span className="sr-only">{t('staff.picker.search')}</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('staff.picker.search')}
              className="min-h-11 w-full rounded-md border border-border bg-surface px-3 text-sm text-text outline-none focus:ring-2 focus:ring-primary"
            />
          </label>
          {!isLoading && !isError && (
            <p className="text-xs text-text-muted" role="status">
              {filtered.length === 1
                ? t('staff.picker.count_one')
                : t('staff.picker.count', { n: filtered.length })}
            </p>
          )}
        </div>

        {isLoading ? (
          <div className="rounded-lg border border-border bg-surface p-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonRow key={i} />
            ))}
          </div>
        ) : isError ? (
          <div className="rounded-lg border border-notification bg-notification-soft p-5 text-sm text-notification">
            <p>
              {t('staff.picker.error')}
              {error instanceof Error ? ` ${error.message}` : ''}
            </p>
            <button
              type="button"
              onClick={() => void refetch()}
              className="mt-2 min-h-11 text-sm font-medium underline md:min-h-6"
            >
              {t('common.retry')}
            </button>
          </div>
        ) : (users ?? []).length === 0 ? (
          <div className="rounded-lg border border-border bg-surface">
            <EmptyState
              icon={<UsersIcon />}
              title={t('staff.picker.empty')}
              subtitle={t('staff.picker.empty_hint')}
            />
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-lg border border-border bg-surface p-8 text-center text-sm text-text-muted">
            {t('staff.picker.no_results')}
          </div>
        ) : (
          <>
            {/* Phone layout: one card per user */}
            <ul className="space-y-3 md:hidden">
              {filtered.map((portalUser) => (
                <li key={portalUser.user_id} className="rounded-lg border border-border bg-surface p-4">
                  <p className="font-medium text-text">{portalUser.full_name ?? portalUser.email}</p>
                  {portalUser.full_name && (
                    <p className="text-sm text-text-muted break-all">{portalUser.email}</p>
                  )}
                  <p className="mt-2 text-xs text-text-muted">
                    <AccountSummary user={portalUser} />
                  </p>
                  <p className="mt-1 text-xs text-text-faint">
                    {t('staff.picker.last_login')}:{' '}
                    {portalUser.last_login_at
                      ? formatDateTime(portalUser.last_login_at)
                      : t('staff.picker.never')}
                  </p>
                  <button
                    type="button"
                    onClick={() => choose(portalUser)}
                    className="mt-3 inline-flex min-h-11 w-full items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-text-inverse hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    {t('staff.picker.view')}
                  </button>
                </li>
              ))}
            </ul>

            {/* Desktop layout */}
            <div className="hidden overflow-hidden rounded-lg border border-border bg-surface md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs text-text-muted">
                    <th scope="col" className="px-4 py-3 font-medium">{t('staff.picker.col_user')}</th>
                    <th scope="col" className="px-4 py-3 font-medium">{t('staff.picker.col_accounts')}</th>
                    <th scope="col" className="px-4 py-3 font-medium">{t('staff.picker.last_login')}</th>
                    <th scope="col" className="px-4 py-3">
                      <span className="sr-only">{t('staff.picker.view')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-divider">
                  {filtered.map((portalUser) => (
                    <tr key={portalUser.user_id} className="text-text">
                      <td className="px-4 py-3 align-top">
                        <p className="font-medium">{portalUser.full_name ?? portalUser.email}</p>
                        {portalUser.full_name && (
                          <p className="text-xs text-text-muted">{portalUser.email}</p>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-text-muted">
                        <AccountSummary user={portalUser} />
                      </td>
                      <td className="px-4 py-3 align-top text-text-muted">
                        {portalUser.last_login_at
                          ? formatDateTime(portalUser.last_login_at)
                          : t('staff.picker.never')}
                      </td>
                      <td className="px-4 py-3 text-right align-top">
                        <button
                          type="button"
                          onClick={() => choose(portalUser)}
                          className="inline-flex min-h-9 items-center rounded-md bg-primary px-3 text-sm font-medium text-text-inverse hover:bg-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                        >
                          {t('staff.picker.view')}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

function AccountSummary({ user }: { user: PortalUser }) {
  const { t } = useI18n();
  const shown = user.account_names.slice(0, MAX_ACCOUNT_NAMES);
  const extra = user.account_names.length - shown.length;
  return (
    <>
      <span className="font-medium text-text">
        {user.active_accounts === 1
          ? t('viewas.account_one')
          : t('viewas.accounts', { n: user.active_accounts })}
      </span>
      {shown.length > 0 && (
        <span>
          {' · '}
          {shown.join(', ')}
          {extra > 0 ? ` ${t('staff.picker.more', { n: extra })}` : ''}
        </span>
      )}
    </>
  );
}

function UsersIcon() {
  return (
    <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M17 20h5v-2a3 3 0 0 0-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 0 1 5.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 0 1 9.288 0M15 7a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM7 10a2 2 0 1 1-4 0 2 2 0 0 1 4 0Z"
      />
    </svg>
  );
}
